import React, { useEffect, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import customStyles from './CustomTableStyles';
import CustomExpander from './CustomExpander/CustomExpander';
import AdvancedFilter from './AdvancedFilter/AdvancedFilter';

import { useAddEncounterOnMapModalStore } from 'pages/InteractiveMap/components/Modals/AddEncOnMap/AddEncounterOnMapModalStore';
import GreenAddButton from 'components/Button/GreenAddButton';
import CustomPagination from 'components/Common/PaginationDataTable';
import Search from 'components/Search/Search';
import { MLSelect } from 'components/Select/Select';

interface AdvancedFilterDataTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  isLoading: boolean;
  amountPerPageLabel?: string;
  onChangePage: (page: number) => void;
  onChangeFilterParams: (ordering: string) => void;
  onChangeSearch: (search: string) => void;
  searchFields: string[];
  expandableRows?: boolean;
  expandableRowsComponent?: React.FC<{ data: T }>;
  expandableRowsComponentProps?: {
    slug: string;
  };
  onChangeAdvancedFilter?: (filterType: string) => void;
  isEncountersTable?: boolean;
  isSighting?: boolean;
  isNotReportsTable?: boolean;
}

function AdvancedFilterDataTable<T>({
  columns,
  data,
  onChangeFilterParams,
  onChangeSearch,
  searchFields,
  amountPerPageLabel = 'Traps Per Page',
  isLoading,
  expandableRows = false,
  expandableRowsComponent,
  expandableRowsComponentProps,
  onChangeAdvancedFilter,
  isEncountersTable = false,
  isSighting = false,
  isNotReportsTable = true,
}: AdvancedFilterDataTableProps<T>) {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { onManageAddEncounterOnMapOpen } = useAddEncounterOnMapModalStore();
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterType, setFilterType] = useState('default');
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => {
    let filtered = data.filter(item =>
      searchFields.some(column => {
        const value = item[column as keyof T];
        return (
          value === null || (value !== undefined && value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }),
    );

    if (sortField) {
      filtered = filtered.sort((a, b) => {
        const aValue = a[sortField as keyof T];
        const bValue = b[sortField as keyof T];

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(filtered);
  }, [searchTerm, data, sortField, sortDirection, searchFields]);
  useEffect(() => {
    setCurrentPage(1);
    if (onChangeAdvancedFilter) {
      onChangeAdvancedFilter(filterType);
    }
  }, [filterType, onChangeAdvancedFilter]);
  const handleItemsPerPageChange = (value: string | number | null) => {
    const selectedValue = Number(value) || 10;
    setItemsPerPage(selectedValue);
    setCurrentPage(1);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    onChangeSearch(search);
    setCurrentPage(1);
  };

  const handleSort = (column: TableColumn<T>, sortDirection: 'asc' | 'desc') => {
    setSortField(column.sortField);
    setSortDirection(sortDirection);
    onChangeFilterParams(sortDirection === 'asc' ? column.sortField || '' : `-${column.sortField}`);
    setCurrentPage(1);
  };

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center justify-content-center gap-3">
          <MLSelect
            options={[
              { value: 10, label: '10' },
              { value: 20, label: '20' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ]}
            label={amountPerPageLabel}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          />
          <div className="pt-4">
            <Search onSearch={handleSearch} />
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-center gap-3">
          {isEncountersTable && (
            <GreenAddButton
              title={t('ObjectVuls.table.btn-add-pest-sigthings')}
              onClick={() => onManageAddEncounterOnMapOpen(slug || ' ')}
            />
          )}
          {isNotReportsTable && (
            <AdvancedFilter filterType={filterType} setFilterType={setFilterType} isSighting={isSighting} />
          )}
        </div>
      </div>
      <DataTable
        columns={columns}
        data={paginatedData}
        progressPending={isLoading}
        pagination={false}
        customStyles={customStyles}
        dense
        onSort={handleSort}
        sortServer
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent}
        expandableRowsComponentProps={expandableRowsComponentProps}
        expandableIcon={{
          collapsed: <CustomExpander isExpanded={false} />,
          expanded: <CustomExpander isExpanded />,
        }}
      />
      <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
}

export default AdvancedFilterDataTable;
